/**
 * Customer pet page card: evaluation status + what an evaluation involves
 * (tenant-authored `assessment_note_html`) + the "book one" call to action
 * (migration 0099). Renders nothing when the tenant does not gate anything
 * and the pet has no evaluation history.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { customerApi } from '../customer_api';
import {
  type CustomerAssessmentSettings,
  type CustomerPetAssessment,
  TenantHtml,
} from '../pages/AssessmentBook';

const STATUS_COPY: Record<string, string> = {
  not_required: 'No evaluation on file yet.',
  required: 'An evaluation day is needed before booking.',
  scheduled: 'Evaluation day booked.',
  passed: 'Evaluation passed.',
  failed: 'Evaluation not passed — please get in touch.',
  expired: 'Evaluation expired — a new evaluation day is needed.',
};

export function PetAssessmentCard({ petId }: { petId: string }) {
  const [settings, setSettings] = useState<CustomerAssessmentSettings | null>(null);
  const [pet, setPet] = useState<CustomerPetAssessment | null>(null);

  useEffect(() => {
    Promise.all([
      customerApi<CustomerAssessmentSettings>('/api/customer/assessments/settings'),
      customerApi<CustomerPetAssessment>(`/api/customer/assessments/pets/${petId}`),
    ])
      .then(([s, p]) => {
        setSettings(s);
        setPet(p);
      })
      .catch(() => {
        /* card is optional */
      });
  }, [petId]);

  if (!settings || !pet) return null;
  const gated = settings.daycareRequiresAssessment || settings.boardingRequiresAssessment;
  if (!gated && pet.status === 'not_required') return null;

  const needsBooking = !pet.isCurrent && pet.status !== 'scheduled' && pet.status !== 'failed';

  return (
    <div
      data-testid="pet-assessment-card"
      style={{
        marginTop: '1.5rem',
        padding: '1.25rem',
        background: 'var(--surface, white)',
        borderRadius: '12px',
        border: '1px solid var(--border, oklch(0.915 0.005 80))',
      }}
    >
      <div
        style={{
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          marginBottom: '0.75rem',
        }}
      >
        Evaluation day
      </div>
      <p style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', fontWeight: 500 }}>
        {STATUS_COPY[pet.status] ?? pet.status}
      </p>
      {pet.status === 'passed' && pet.expiresAt && (
        <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', color: 'var(--muted-foreground)' }}>
          Valid until{' '}
          <span className="num">{new Date(pet.expiresAt).toLocaleDateString('en-GB')}</span>
        </p>
      )}
      {gated && (
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: 'var(--muted-foreground)' }}>
          Required for{' '}
          {[
            settings.daycareRequiresAssessment ? 'daycare' : null,
            settings.boardingRequiresAssessment ? 'boarding' : null,
          ]
            .filter(Boolean)
            .join(' and ')}
          .
        </p>
      )}
      {settings.noteHtml && (
        <div style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
          <TenantHtml html={settings.noteHtml} />
        </div>
      )}
      {pet.openBooking && (
        <p style={{ margin: 0, fontSize: '0.85rem' }}>
          <Link to={`/account/bookings/${pet.openBooking.bookingId}`}>View the booking</Link>
        </p>
      )}
      {needsBooking && pet.canBook && (
        <Link
          to={pet.bookAssessmentUrl}
          style={{
            display: 'inline-block',
            padding: '0.5rem 0.9rem',
            borderRadius: 8,
            background: 'var(--primary, #004AAD)',
            color: 'white',
            fontSize: '0.85rem',
            fontWeight: 500,
            textDecoration: 'none',
          }}
        >
          Book an evaluation day
        </Link>
      )}
    </div>
  );
}
